import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { FactoredRemainingLifeCell } from '../../viewmodels/admin/risk-matrices/factored-remaining-life-cell';
import { FactoredRemainingLifeMatrix } from '../../viewmodels/admin/risk-matrices/factored-remaining-life-matrix';
import { RiskMatrix, RiskMatrixConsequencePosition } from '../../viewmodels/admin/risk-matrices/risk-matrix';
import { RiskMatrixCell } from '../../viewmodels/admin/risk-matrices/risk-matrix-cell';
import { RiskMatrixConsequenceVector } from '../../viewmodels/admin/risk-matrices/risk-matrix-consequence-vector';
import { RiskMatrixProbabilityVector } from '../../viewmodels/admin/risk-matrices/risk-matrix-probability-vector';
import { RiskMatrixVector } from '../../viewmodels/admin/risk-matrices/risk-matrix-vector';
import { MatrixCalibration } from '../../viewmodels/analysis/matrix-calibration';
import { ConsequenceAxisLabelService } from './consequence-axis-label.service';

@Component({
  selector: 'hb-risk-matrix-grid',
  templateUrl: './risk-matrix-grid.component.html',
  styleUrls: ['./risk-matrix-grid.component.scss'],
})
export class RiskMatrixGridComponent implements OnInit, OnChanges {
  @Input() public riskMatrix: RiskMatrix;
  @Input() public isEditable = false;
  @Input() public useFrl = false;
  @Input() public drawCircle: boolean = false;
  @Input() public matrixCalibration: MatrixCalibration;

  @Input() private frlMatrix: FactoredRemainingLifeMatrix;
  @Input() private useMassCriticalityResults: boolean = false;
  @Input() private massCriticalityResultCellIds: string[] = [];
  @Input() private alwaysDisplayCellLabels: boolean = false;

  @Output() private onColumnLabelClicked = new EventEmitter<ColumnLabelClickArgs>();
  @Output() private onRowLabelClicked = new EventEmitter<RowLabelClickArgs>();
  @Output() private onCellClicked = new EventEmitter<CellClickArgs>();
  @Output() private onFrlCellClicked = new EventEmitter<FrlCellClickArgs>();

  public display: string = 'inline';

  public gridRows: RiskMatrixVector[] = [];
  public gridCols: RiskMatrixVector[] = [];

  public rowVectorId: string;
  public colVectorId: string;

  constructor(
    private consequenceAxisLabelService: ConsequenceAxisLabelService,
    private i18NextPipe: I18NextPipe
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.riskMatrix) {
      this.setGridRows();
      this.setGridcols();
    }

    if (changes?.matrixCalibration || changes?.riskMatrix) {
      this.setRowVectorId();
      this.setColVectorId();
    }
  }

  public ngOnInit(): void {
    if (!this.riskMatrix) {
      console.error('Risk matrix input is missing for RiskMatrixGridComponent.');

      return;
    }

    if (!this.alwaysDisplayCellLabels && this.drawCircle) {
      this.display = 'none';
    }
  }

  private setRowVectorId() {
    if (!this.matrixCalibration) {
      this.rowVectorId = '';
      return;
    }

    this.rowVectorId = this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns
      ? this.matrixCalibration.riskMatrixProbabilityVectorId
      : this.matrixCalibration.riskMatrixConsequenceVectorId;
  }

  private setColVectorId(): string {
    if (!this.matrixCalibration) {
      this.colVectorId = ''
      return;
    }

    this.colVectorId = this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns
      ? this.matrixCalibration.riskMatrixConsequenceVectorId
      : this.matrixCalibration.riskMatrixProbabilityVectorId;
  }

  public getXAxisLabel(): string {
    return this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns ?
      this.i18NextPipe.transform('RiskMatrixGrid.Consequence') :
      this.i18NextPipe.transform('RiskMatrixGrid.Probability');
  }

  public getYAxisLabel(): string {
    return this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns ?
      this.i18NextPipe.transform('RiskMatrixGrid.Probability') :
      this.i18NextPipe.transform('RiskMatrixGrid.Consequence');
  }

  public getXAxisColumnLabel(column): string {
    if (column.label) {
      return column.label;
    }

    if (this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns) {
      return this.consequenceAxisLabelService.getLetterBasedLabel(column.index);
    } else {
      return String(column.index + 1);
    }
  }

  public getYAxisRowLabel(row: RiskMatrixVector): string {
    if (row.label) {
      return row.label;
    }

    if (this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns) {
      return String(row.index + 1);
    } else {
      return this.consequenceAxisLabelService.getLetterBasedLabel(row.index);
    }
  }

  public columnLabelClicked(event, column): void {
    this.onColumnLabelClicked.next({
      event,
      column,
    });
  }

  public rowLabelClicked(event, row): void {
    this.onRowLabelClicked.next({
      event,
      row,
    });
  }

  public cellClicked(event, cell: RiskMatrixCell): void {
    if (this.useFrl) {
      this.onFrlCellClicked.next({
        event,
        cell: this.frlMatrix.factoredRemainingLifeCells.find(x => x.riskMatrixCellId === cell.riskMatrixCellId),
      });
    } else {
      this.onCellClicked.next({
        event,
        cell,
      });
    }
  }

  private setGridcols() {
    this.gridCols = this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns
      ? this.riskMatrix.consequenceVectors.slice().reverse()
      : this.riskMatrix.probabilityVectors.slice().reverse();
  }

  private setGridRows() {
    this.gridRows = this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.rows
      ? this.riskMatrix.consequenceVectors
      : this.riskMatrix.probabilityVectors;
  }

  public getCell(column: RiskMatrixVector, row: RiskMatrixVector): RiskMatrixCell {
    const consequenceIndex = this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns ? column.index : row.index;
    const probabilityIndex = this.riskMatrix.consequencePosition === RiskMatrixConsequencePosition.columns ? row.index : column.index;

    const cell = this.riskMatrix.probabilityVectors[probabilityIndex].cells[consequenceIndex];

    if (this.useMassCriticalityResults && this.massCriticalityResultCellIds) {
      let count: number = 0;

      this.massCriticalityResultCellIds.forEach(id => {
        if (cell.riskMatrixCellId === id) {
          count++;
        }
      });
      cell.label = String(count);
    }

    return cell;
  }

  public displayFrlCell(column: RiskMatrixVector, row: RiskMatrixVector): boolean {
    if (this.frlMatrix) {
      const value = this.getFrlCell(column, row);
      const display = this.useFrl &&
        this.frlMatrix.factoredRemainingLifeCells.length > 0 &&
        !!value;

      return display;
    }
  }

  public getFrlCell(column: RiskMatrixVector, row: RiskMatrixVector): FactoredRemainingLifeCell {
    const cell = this.frlMatrix.factoredRemainingLifeCells.find(x => x.riskMatrixCellId === this.getCell(column, row).riskMatrixCellId);

    return cell;
  }

  public getRiskBandColor(column, row): string {
    const cell = this.getCell(column, row);

    return cell.riskBand.color;
  }

}

export interface ColumnLabelClickArgs {
  event: any;
  column: RiskMatrixConsequenceVector;
}

export interface RowLabelClickArgs {
  event: any;
  row: RiskMatrixProbabilityVector;
}

export interface CellClickArgs {
  event: any;
  cell: RiskMatrixCell;
}

export interface FrlCellClickArgs {
  event: any;
  cell: FactoredRemainingLifeCell;
}
